import { useState } from "react";
import { Link } from "react-router-dom";
import { BrandLockup } from "@/components/layout/BrandLockup";
import { useProfileStore } from "@/state/profileStore";
import { PLANS, TRIAL_DAYS } from "@/config/plans";
import { SurveyModal } from "@/components/SurveyModal";

const SURVEY_MODE = import.meta.env.VITE_SURVEY_MODE === "true";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1609868900839-f7a9e3d1a52d?w=1800&q=86";

function readStoredMetric(key: string, fallback: number) {
  if (typeof window === "undefined") return fallback;
  const saved = window.localStorage.getItem(key);
  return saved ? Number(saved) : fallback;
}

const previewDays = [
  {
    day: "MON",
    title: "Recovery",
    meta: "35 min",
    bars: [18, 18, 18, 18, 18],
    tone: "#80a79d",
  },
  {
    day: "TUE",
    title: "Threshold 4×8",
    meta: "72 min · 86 TSS",
    bars: [20, 38, 70, 38, 70, 38, 70, 38, 70, 24],
    tone: "#007d70",
  },
  {
    day: "WED",
    title: "Endurance",
    meta: "60 min · 42 TSS",
    bars: [20, 26, 31, 29, 34, 30, 27, 22],
    tone: "#4e7b72",
  },
  {
    day: "THU",
    title: "Rest day",
    meta: "Recover & adapt",
    bars: [],
    tone: "#9eaaa6",
  },
];

export function LandingPage() {
  const profileFtp = useProfileStore((s) => s.profile?.ftpWatts);
  const [localFtp, setLocalFtp] = useState<number | "">(() =>
    readStoredMetric("landing_ftp", 285),
  );
  const [tss, setTss] = useState<number | "">(() =>
    readStoredMetric("landing_tss", 420),
  );
  const [editingFtp, setEditingFtp] = useState(false);
  const [editingTss, setEditingTss] = useState(false);
  const [surveyOpen, setSurveyOpen] = useState(false);
  const ftp: number | "" = profileFtp ?? localFtp;

  function saveFtp(value: number | "") {
    if (value !== "") localStorage.setItem("landing_ftp", String(value));
    setLocalFtp(value);
    setEditingFtp(false);
  }

  function saveTss(value: number | "") {
    if (value !== "") localStorage.setItem("landing_tss", String(value));
    setTss(value);
    setEditingTss(false);
  }

  return (
    <div className="min-h-screen bg-[#f3f5f2] text-[#10201d] selection:bg-[#bde7dc] selection:text-[#10201d]">
      <nav
        aria-label="Site navigation"
        className="sticky top-0 z-30 border-b border-[#d7dfdb] bg-[#f3f5f2]/95 backdrop-blur-xl"
      >
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-2 px-3 sm:px-6 lg:px-8">
          <Link
            to="/"
            aria-label="Momentum Peaks home"
            className="min-w-0 shrink-0"
          >
            <span className="sm:hidden"><BrandLockup size="sm" /></span>
            <span className="hidden sm:inline"><BrandLockup size="lg" /></span>
          </Link>
          <div className="flex shrink-0 items-center gap-1.5 sm:gap-3">
            <a
              href="#how-it-works"
              className="hidden px-3 py-2 text-sm font-semibold text-[#435650] hover:text-[#10201d] md:block"
            >
              How it works
            </a>
            <Link
              to="/login"
              className="whitespace-nowrap rounded-lg px-2.5 py-2 text-[13px] font-bold text-[#435650] transition-colors hover:bg-[#006b5f]/10 hover:text-[#00574e] sm:border sm:border-[#aebdb7] sm:px-4 sm:text-sm"
            >
              Log in
            </Link>
            {SURVEY_MODE ? (
              <button
                onClick={() => setSurveyOpen(true)}
                className="whitespace-nowrap rounded-lg bg-[#006b5f] px-3.5 py-2.5 text-[13px] font-bold text-[#ffffff] shadow-[0_1px_2px_rgba(10,32,28,0.18)] transition-[background-color,transform] hover:-translate-y-0.5 hover:bg-[#00574e] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#006b5f] sm:px-5 sm:text-sm"
              >
                Start free
              </button>
            ) : (
              <Link
                to="/billing"
                className="whitespace-nowrap rounded-lg bg-[#006b5f] px-3.5 py-2.5 text-[13px] font-bold text-[#ffffff] shadow-[0_1px_2px_rgba(10,32,28,0.18)] transition-[background-color,transform] hover:-translate-y-0.5 hover:bg-[#00574e] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#006b5f] sm:px-5 sm:text-sm"
              >
                Start free
              </Link>
            )}
          </div>
        </div>
      </nav>

      <main>
        <section className="mx-auto max-w-7xl px-4 pb-10 pt-6 sm:px-6 sm:pb-14 sm:pt-10 lg:px-8 lg:pt-12">
          <div className="overflow-hidden border border-[#aebdb7] bg-[#ecefe9] shadow-[0_20px_60px_rgba(16,32,29,0.11)]">
            <div className="flex items-center justify-between border-b border-[#aebdb7] px-4 py-3 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-[#52645f] sm:px-6">
              <span>MP / Training dossier 001</span>
              <span className="hidden sm:inline">Target: peak form</span>
              <span>Season 26</span>
            </div>

            <div className="grid lg:grid-cols-[84px_1.08fr_0.92fr]">
              <aside
                className="hidden border-r border-[#aebdb7] lg:flex lg:flex-col lg:items-center lg:justify-between lg:py-8"
                aria-label="Training dossier marker"
              >
                <span className="font-mono text-xs font-bold text-[#006b5f]">
                  01
                </span>
                <span className="[writing-mode:vertical-rl] rotate-180 font-mono text-[10px] font-bold uppercase tracking-[0.28em] text-[#60716c]">
                  Build phase / eight weeks
                </span>
                <span className="h-2 w-2 rounded-full bg-[#007d70]" />
              </aside>

              <div className="relative flex min-h-[600px] flex-col justify-between overflow-hidden px-6 py-10 sm:px-10 sm:py-14 lg:px-12 lg:py-16">
                <svg
                  className="pointer-events-none absolute -bottom-5 -right-24 h-64 w-[620px] text-[#a7b9b3] opacity-55"
                  viewBox="0 0 620 250"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M8 206C55 190 75 215 118 193C166 168 188 117 226 134C269 154 284 76 326 90C361 102 389 156 421 128C452 101 468 33 510 48C550 62 561 113 612 87"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeDasharray="4 8"
                  />
                  <path d="M8 226H612" stroke="currentColor" strokeWidth="1" />
                  {[60, 170, 280, 390, 500].map((x) => (
                    <path key={x} d={`M${x} 219V233`} stroke="currentColor" />
                  ))}
                </svg>
                <div className="relative">
                  <div className="flex items-center gap-3 font-mono text-[11px] font-bold uppercase tracking-[0.15em] text-[#006b5f]">
                    <span className="h-px w-8 bg-[#006b5f]" /> FTP-led endurance
                    planning
                  </div>
                  <h1 className="mt-8 max-w-3xl text-[3.1rem] font-black leading-[0.88] tracking-[-0.065em] text-[#10201d] sm:text-7xl lg:text-[5.4rem]">
                    Arrive ready.
                    <br />
                    <span className="text-[#006b5f]">Not lucky.</span>
                  </h1>
                  <p className="mt-7 max-w-lg text-base leading-7 text-[#435650] sm:text-lg">
                    A living training plan shaped by your power, your available
                    days, and the ride circled on your calendar.
                  </p>
                </div>

                <div className="relative mt-12">
                  <div className="mb-6 grid max-w-lg grid-cols-3 border-y border-[#bdc9c4] py-4 font-mono">
                    <div>
                      <span className="block text-[9px] uppercase tracking-[0.16em] text-[#667771]">
                        Baseline
                      </span>
                      <strong className="mt-1 block text-base text-[#18312c]">
                        FTP {ftp}W
                      </strong>
                    </div>
                    <div className="border-x border-[#bdc9c4] px-4">
                      <span className="block text-[9px] uppercase tracking-[0.16em] text-[#667771]">
                        Build
                      </span>
                      <strong className="mt-1 block text-base text-[#18312c]">
                        08 weeks
                      </strong>
                    </div>
                    <div className="pl-4">
                      <span className="block text-[9px] uppercase tracking-[0.16em] text-[#667771]">
                        Output
                      </span>
                      <strong className="mt-1 block text-base text-[#18312c]">
                        .FIT ready
                      </strong>
                    </div>
                  </div>
                  <div className="flex flex-col gap-3 sm:flex-row">
                    {SURVEY_MODE ? (
                      <button
                        onClick={() => setSurveyOpen(true)}
                        className="inline-flex min-h-12 items-center justify-between gap-8 bg-[#006b5f] px-5 py-3 text-sm font-extrabold text-[#ffffff] shadow-[4px_4px_0_#9db7af] transition-[transform,box-shadow] hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-[2px_2px_0_#9db7af] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#006b5f]"
                      >
                        Start {TRIAL_DAYS}-day field test{" "}
                        <span aria-hidden="true">→</span>
                      </button>
                    ) : (
                      <Link
                        to="/billing?plan=monthly"
                        className="inline-flex min-h-12 items-center justify-between gap-8 bg-[#006b5f] px-5 py-3 text-sm font-extrabold text-[#ffffff] shadow-[4px_4px_0_#9db7af] transition-[transform,box-shadow] hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-[2px_2px_0_#9db7af] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#006b5f]"
                      >
                        Start {TRIAL_DAYS}-day field test{" "}
                        <span aria-hidden="true">→</span>
                      </Link>
                    )}
                    <a
                      href="#product-preview"
                      className="inline-flex min-h-12 items-center justify-center border border-[#9fafa9] bg-[#f8faf7] px-5 py-3 text-sm font-extrabold text-[#18312c] transition-colors hover:bg-[#dfe7e3] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#006b5f]"
                    >
                      Open a sample week
                    </a>
                  </div>
                  <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.1em] text-[#60716c]">
                    No card · Cancel anytime · Garmin + Wahoo
                  </p>
                </div>
              </div>

              <div className="relative min-h-[560px] overflow-hidden border-t border-[#aebdb7] lg:min-h-[720px] lg:border-l lg:border-t-0">
                <img
                  src={HERO_IMAGE}
                  alt="Cyclist training on an open mountain road"
                  className="absolute inset-0 h-full w-full object-cover"
                />
                <div
                  className="absolute inset-0 bg-black/35"
                  aria-hidden="true"
                />
                <div className="absolute left-4 top-4 border border-[rgba(255,255,255,0.45)] bg-[#0a1917]/80 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-[#ffffff] backdrop-blur-sm sm:left-6 sm:top-6">
                  On course / 06:14
                </div>

                <div className="absolute inset-x-4 bottom-4 bg-[#0a1917]/94 text-[#ffffff] shadow-[8px_8px_0_rgba(223,235,230,0.28)] backdrop-blur-md sm:inset-x-6 sm:bottom-6">
                  <div className="grid grid-cols-[1fr_auto] border-b border-[rgba(255,255,255,0.22)]">
                    <div className="p-5 sm:p-6">
                      <span className="font-mono text-[10px] font-bold uppercase tracking-[0.16em] text-[#92d8c7]">
                        Tuesday / Key session
                      </span>
                      <h2 className="mt-2 text-2xl font-extrabold leading-tight tracking-[-0.03em] text-[#ffffff] sm:text-3xl">
                        Threshold repeatability
                      </h2>
                    </div>
                    <div className="flex w-16 items-center justify-center border-l border-[rgba(255,255,255,0.22)] font-mono text-xl font-bold text-[#b9f1e3]">
                      A
                    </div>
                  </div>
                  <div className="grid grid-cols-3 divide-x divide-[rgba(255,255,255,0.18)] font-mono">
                    <div className="p-4">
                      <span className="block text-[9px] uppercase text-[#a9bbb6]">
                        Work
                      </span>
                      <strong className="mt-1 block text-base text-[#ffffff]">
                        4×8
                      </strong>
                    </div>
                    <div className="p-4">
                      <span className="block text-[9px] uppercase text-[#a9bbb6]">
                        Target
                      </span>
                      <strong className="mt-1 block text-base text-[#ffffff]">
                        96% FTP
                      </strong>
                    </div>
                    <div className="p-4">
                      <span className="block text-[9px] uppercase text-[#a9bbb6]">
                        Time
                      </span>
                      <strong className="mt-1 block text-base text-[#ffffff]">
                        72 min
                      </strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section
          aria-label="Live plan signal"
          className="border-y border-[#102a25] bg-[#102a25] text-[#ffffff]"
        >
          <div className="mx-auto flex max-w-7xl flex-col font-mono sm:flex-row sm:items-stretch sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 border-b border-[rgba(255,255,255,0.16)] px-5 py-4 sm:border-b-0 sm:border-r">
              <span className="h-2 w-2 rounded-full bg-[#65d8bf]" />
              <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#b9c9c5]">
                Plan signal / live
              </span>
            </div>
            {[
              ["W04", "Build phase"],
              [String(tss), "Weekly TSS"],
              ["+6", "Form at event"],
              ["SYNC", "Device ready"],
            ].map(([value, label]) => (
              <div
                key={label}
                className="flex flex-1 items-center justify-between border-b border-[rgba(255,255,255,0.16)] px-5 py-4 last:border-0 sm:block sm:border-b-0 sm:border-r sm:text-center"
              >
                <strong className="text-base text-[#ffffff]">{value}</strong>
                <span className="text-[9px] uppercase tracking-[0.14em] text-[#9fb3ae] sm:mt-1 sm:block">
                  {label}
                </span>
              </div>
            ))}
          </div>
        </section>

        <section
          id="product-preview"
          className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8"
        >
          <div className="grid items-start gap-10 lg:grid-cols-[0.72fr_1.28fr] lg:gap-16">
            <div className="lg:sticky lg:top-28">
              <p className="font-mono text-xs font-bold uppercase tracking-[0.16em] text-[#006b5f]">
                Week file / 04
              </p>
              <h2 className="mt-4 text-3xl font-extrabold leading-tight tracking-[-0.035em] text-[#10201d] sm:text-5xl">
                Your week, readable in one glance.
              </h2>
              <p className="mt-5 text-base leading-7 text-[#52645f]">
                The detail is there when you need it—not before. Friendly
                interval shapes show the rhythm of each ride, while load and
                duration stay instantly scannable.
              </p>
              <div className="mt-8 grid grid-cols-2 gap-3">
                <EditableMetric
                  label="Your FTP"
                  value={ftp}
                  suffix="W"
                  editing={editingFtp}
                  onEdit={() => setEditingFtp(true)}
                  onChange={setLocalFtp}
                  onSave={() => saveFtp(ftp)}
                />
                <EditableMetric
                  label="Weekly load"
                  value={tss}
                  suffix="TSS"
                  editing={editingTss}
                  onEdit={() => setEditingTss(true)}
                  onChange={setTss}
                  onSave={() => saveTss(tss)}
                />
              </div>
              <p className="mt-3 text-xs text-[#667771]">
                Tap a metric to make this preview yours.
              </p>
            </div>

            <div className="rounded-[26px] border border-[#bfccc7] bg-[#dfe7e3] p-2 shadow-[0_20px_50px_rgba(16,32,29,0.12),inset_0_1px_0_rgba(255,255,255,0.8)] sm:p-3">
              <div className="overflow-hidden rounded-[20px] border border-[#cbd6d1] bg-[#fafbf9] shadow-[0_2px_7px_rgba(16,32,29,0.08)]">
                <div className="flex flex-col gap-4 border-b border-[#d7dfdb] px-5 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-7">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#60716c]">
                      Training block · Week 4
                    </p>
                    <h3 className="mt-1 text-xl font-bold tracking-[-0.02em] text-[#10201d] sm:text-2xl">
                      Build sustainable power
                    </h3>
                  </div>
                  <div className="flex gap-2">
                    <span className="rounded-lg border border-[#b8c6c1] bg-[#ffffff] px-3 py-2 font-mono text-xs font-bold text-[#344844]">
                      4 rides
                    </span>
                    <span className="rounded-lg border border-[#9cc2b8] bg-[#e5f2ee] px-3 py-2 font-mono text-xs font-bold text-[#005b51]">
                      310 TSS
                    </span>
                  </div>
                </div>
                <div className="space-y-2 p-3 sm:p-5">
                  {previewDays.map((item, index) => (
                    <article
                      key={item.day}
                      className="group grid grid-cols-[46px_1fr] gap-3 rounded-2xl border border-[#d5ddda] bg-[#ffffff] p-3 shadow-[0_1px_2px_rgba(16,32,29,0.05)] transition-[border-color,box-shadow,transform] hover:-translate-y-0.5 hover:border-[#94b7ae] hover:shadow-[0_8px_22px_rgba(16,32,29,0.10)] sm:grid-cols-[58px_1fr_210px] sm:items-center sm:p-4"
                    >
                      <div className="flex h-11 items-center justify-center rounded-xl bg-[#edf2ef] font-mono text-[11px] font-bold tracking-[0.1em] text-[#52645f]">
                        {item.day}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className="truncate text-sm font-bold text-[#18312c] sm:text-base">
                            {item.title}
                          </h4>
                          {index === 1 && (
                            <span className="rounded-full bg-[#d9eee8] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#005b51]">
                              Key
                            </span>
                          )}
                        </div>
                        <p className="mt-1 font-mono text-[11px] text-[#60716c]">
                          {item.meta}
                        </p>
                      </div>
                      <div className="col-span-2 flex h-14 items-end gap-[3px] rounded-xl border border-[#e1e7e4] bg-[#f4f7f5] px-3 py-2 sm:col-span-1">
                        {item.bars.length ? (
                          item.bars.map((height, barIndex) => (
                            <span
                              key={`${item.day}-${barIndex}`}
                              className="min-w-[6px] flex-1"
                              style={{
                                height: `${height}%`,
                                backgroundColor: item.tone,
                                borderRadius: "2px 2px 0 0",
                                opacity: 0.85,
                              }}
                            />
                          ))
                        ) : (
                          <div className="flex h-full w-full items-center gap-2 text-xs font-semibold text-[#73827d]">
                            <span className="h-2 w-2 rounded-full bg-[#aab6b2]" />{" "}
                            No intervals planned
                          </div>
                        )}
                      </div>
                    </article>
                  ))}
                </div>
                <div className="flex items-center justify-between border-t border-[#d7dfdb] bg-[#f1f4f2] px-5 py-4 sm:px-7">
                  <span className="text-xs font-semibold text-[#52645f]">
                    Plan adjusted to your recovery
                  </span>
                  <span className="flex items-center gap-2 text-xs font-bold text-[#006b5f]">
                    <span className="h-2 w-2 rounded-full bg-[#008f80]" />{" "}
                    Synced
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section
          id="how-it-works"
          className="border-y border-[#bdc9c4] bg-[#e9eeeb] py-16 sm:py-24"
        >
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid gap-10 lg:grid-cols-[0.72fr_1.28fr] lg:gap-20">
              <div>
                <p className="font-mono text-xs font-bold uppercase tracking-[0.16em] text-[#006b5f]">
                  Route to form
                </p>
                <h2 className="mt-4 text-3xl font-extrabold tracking-[-0.04em] text-[#10201d] sm:text-5xl">
                  A plan that behaves like a good coach.
                </h2>
                <p className="mt-5 max-w-md text-base leading-7 text-[#52645f]">
                  It starts with where you are, keeps the destination visible,
                  and changes the route when your week does.
                </p>
              </div>

              <ol className="border-t border-[#8fa29b]">
                {[
                  [
                    "01",
                    "Measure",
                    "Set FTP, available days, and the event that matters.",
                  ],
                  [
                    "02",
                    "Build",
                    "Stack purposeful sessions without losing recovery.",
                  ],
                  [
                    "03",
                    "Respond",
                    "Use each check-in to tune the next week—not rewrite the goal.",
                  ],
                  [
                    "04",
                    "Arrive",
                    "Taper the load and bring your strongest form to the start line.",
                  ],
                ].map(([number, title, copy], index) => (
                  <li
                    key={number}
                    className="group grid grid-cols-[52px_1fr] gap-4 border-b border-[#b8c5c0] py-6 sm:grid-cols-[70px_0.55fr_1fr] sm:items-center"
                  >
                    <span className="font-mono text-xs font-bold text-[#007d70]">
                      {number}
                    </span>
                    <h3 className="text-xl font-extrabold tracking-[-0.025em] text-[#18312c]">
                      {title}
                    </h3>
                    <div className="col-start-2 flex items-center gap-4 sm:col-start-3">
                      <span
                        className={`hidden h-3 w-3 shrink-0 rounded-full border-2 ${index === 3 ? "border-[#006b5f] bg-[#006b5f]" : "border-[#78938c] bg-[#e9eeeb]"} sm:block`}
                      />
                      <p className="text-sm leading-6 text-[#52645f]">{copy}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </section>

        <section
          id="pricing"
          className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8"
        >
          <div className="relative bg-[#102a25] text-[#ffffff] shadow-[12px_12px_0_#c6d4cf]">
            <span
              className="absolute -left-3 top-1/2 h-6 w-6 -translate-y-1/2 rounded-full bg-[#f3f5f2]"
              aria-hidden="true"
            />
            <span
              className="absolute -right-3 top-1/2 h-6 w-6 -translate-y-1/2 rounded-full bg-[#f3f5f2]"
              aria-hidden="true"
            />
            <div className="grid lg:grid-cols-[72px_1fr_0.66fr]">
              <div className="hidden border-r border-dashed border-[rgba(255,255,255,0.32)] lg:flex lg:items-center lg:justify-center">
                <span className="[writing-mode:vertical-rl] rotate-180 font-mono text-[10px] font-bold uppercase tracking-[0.28em] text-[#92d8c7]">
                  Momentum pass / member 001
                </span>
              </div>
              <div className="p-7 sm:p-10 lg:p-12">
                <div className="flex items-center justify-between border-b border-[rgba(255,255,255,0.18)] pb-4 font-mono text-[9px] uppercase tracking-[0.15em] text-[#9fb3ae]">
                  <span>Access / Full platform</span>
                  <span>Valid monthly</span>
                </div>
                <h2 className="mt-8 max-w-xl text-3xl font-extrabold leading-[1.02] tracking-[-0.04em] text-[#ffffff] sm:text-5xl">
                  Your season deserves more than guesswork.
                </h2>
                <ul className="mt-8 grid gap-3 text-sm text-[#d6e0dd] sm:grid-cols-2">
                  {PLANS.monthly.features.slice(0, 6).map((feature) => (
                    <li key={feature} className="flex items-start gap-3">
                      <CheckIcon dark />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="border-t border-dashed border-[rgba(255,255,255,0.32)] bg-[#173a33] p-7 sm:p-10 lg:border-l lg:border-t-0 lg:p-12">
                <p className="font-mono text-[10px] font-bold uppercase tracking-[0.16em] text-[#92d8c7]">
                  {TRIAL_DAYS}-day shakedown
                </p>
                <p className="mt-6 text-xl font-bold tracking-[-0.02em] text-[#ffffff]">
                  Try it free
                </p>
                <p className="mt-4 text-sm leading-6 text-[#c6d3cf]">
                  Build the week. Preview the work. Export the ride. No commitment required.
                </p>
                {SURVEY_MODE ? (
                  <button
                    onClick={() => setSurveyOpen(true)}
                    className="mt-8 inline-flex min-h-12 w-full items-center justify-between bg-[#b9f1e3] px-5 py-3 text-sm font-extrabold text-[#0c3129] shadow-[4px_4px_0_#061512] transition-[transform,box-shadow] hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-[2px_2px_0_#061512] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#ffffff]"
                  >
                    Claim your trial <span aria-hidden="true">→</span>
                  </button>
                ) : (
                  <Link
                    to="/billing?plan=monthly"
                    className="mt-8 inline-flex min-h-12 w-full items-center justify-between bg-[#b9f1e3] px-5 py-3 text-sm font-extrabold text-[#0c3129] shadow-[4px_4px_0_#061512] transition-[transform,box-shadow] hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-[2px_2px_0_#061512] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#ffffff]"
                  >
                    Claim your trial <span aria-hidden="true">→</span>
                  </Link>
                )}
                <p className="mt-4 font-mono text-[9px] uppercase tracking-[0.12em] text-[#a9bbb6]">
                  No card / Cancel anytime
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-[#d7dfdb] bg-[#e9eeeb]">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 px-4 py-8 text-sm text-[#60716c] sm:flex-row sm:items-center sm:justify-between sm:px-6 lg:px-8">
          <span className="font-semibold text-[#344844]">Momentum Peaks</span>
          <span>FTP-based training for cyclists who want clear direction.</span>
        </div>
      </footer>

      {surveyOpen && <SurveyModal onClose={() => setSurveyOpen(false)} />}
    </div>
  );
}

function CheckIcon({ dark = false }: { dark?: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${dark ? "bg-[#28584e] text-[#b9f1e3]" : "bg-[#dbece7] text-[#006b5f]"}`}
    >
      <svg
        viewBox="0 0 20 20"
        className="h-3 w-3"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
      >
        <path d="m5 10 3 3 7-7" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );
}

function EditableMetric({
  label,
  value,
  suffix,
  editing,
  onEdit,
  onChange,
  onSave,
}: {
  label: string;
  value: number | "";
  suffix: string;
  editing: boolean;
  onEdit: () => void;
  onChange: (value: number | "") => void;
  onSave: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onEdit}
      className="rounded-2xl border border-[#c8d2ce] bg-[#fafbf9] p-4 text-left shadow-[0_1px_2px_rgba(16,32,29,0.06)] transition-[border-color,box-shadow] hover:border-[#86aaa1] hover:shadow-[0_5px_15px_rgba(16,32,29,0.08)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#006b5f]"
    >
      <span className="block text-xs font-semibold text-[#60716c]">
        {label}
      </span>
      {editing ? (
        <input
          autoFocus
          type="number"
          value={value}
          onClick={(event) => event.stopPropagation()}
          onChange={(event) =>
            onChange(
              event.target.value === "" ? "" : Number(event.target.value),
            )
          }
          onBlur={onSave}
          onKeyDown={(event) => event.key === "Enter" && onSave()}
          className="mt-2 w-full border-b border-[#78938c] bg-transparent font-mono text-xl font-bold text-[#10201d] outline-none"
          aria-label={label}
        />
      ) : (
        <span className="mt-2 block font-mono text-xl font-bold tracking-[-0.03em] text-[#005b51]">
          {value} <span className="text-xs text-[#60716c]">{suffix}</span>
        </span>
      )}
    </button>
  );
}
